import { useEffect, useState } from "react";

function bySlug(items, slug) {
  return items.find((item) => item.slug === slug) || null;
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function FooterStudio({ items, onSave, onDelete }) {
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const logo = bySlug(items, "footer-logo");
  const note = bySlug(items, "footer-note");
  const company = bySlug(items, "footer-company");
  const labelVisit = bySlug(items, "footer-label-visit");
  const labelFollow = bySlug(items, "footer-label-follow");
  const labelCompany = bySlug(items, "footer-label-company");
  const [headings, setHeadings] = useState({
    visit: "Visit",
    follow: "Follow",
    company: "Company",
  });

  useEffect(() => {
    setHeadings({
      visit: labelVisit?.title || "Visit",
      follow: labelFollow?.title || "Follow",
      company: labelCompany?.title || "Company",
    });
  }, [labelVisit?.title, labelFollow?.title, labelCompany?.title]);

  const phones = items
    .filter((item) => item.kind === "footer" && String(item.link || "").startsWith("tel:"))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const visits = items
    .filter((item) => {
      if (item.kind !== "footer") return false;
      const slug = item.slug || "";
      if (slug === "footer-note" || slug === "footer-company" || slug.startsWith("footer-label-")) return false;
      const link = String(item.link || "");
      return link && !link.startsWith("tel:") && !link.startsWith("mailto:");
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const socials = items
    .filter((item) => item.kind === "social" && item.slug !== "whatsapp")
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  async function submit(event) {
    event.preventDefault();
    setError("");
    const role = draft.role;
    const next = { ...draft };
    delete next.role;
    try {
      if (role === "phone") {
        const digits = String(next.title || "").replace(/\D/g, "");
        if (digits.length < 8) throw new Error("Enter the full phone number, including the country code.");
        next.kind = "footer";
        next.label = next.label || "Footer phone";
        next.slug = next.slug || `footer-phone-${Date.now()}`;
        next.title = String(next.title).trim();
        next.link = `tel:+${digits}`;
      } else if (role === "visit") {
        let link = String(next.link || "").trim();
        if (!next.title?.trim()) throw new Error("Add the words visitors will read.");
        if (!link) throw new Error("Add a link, such as /journeys or a full web address.");
        if (!link.startsWith("/") && !/^https?:/i.test(link) && !link.startsWith("mailto:")) link = `/${link}`;
        next.kind = "footer";
        next.label = next.label || "Footer link";
        next.slug = next.slug || `footer-link-${Date.now()}`;
        next.title = next.title.trim();
        next.link = link;
      } else if (role === "social") {
        let link = String(next.link || "").trim();
        if (!next.title?.trim()) throw new Error("Add the name of the social page.");
        if (!link) throw new Error("Add the web address.");
        if (!/^https?:/i.test(link)) link = `https://${link}`;
        const slug = next.slug || slugify(next.title);
        if (!slug || slug === "whatsapp") throw new Error("Use the WhatsApp tab for the green button.");
        next.kind = "social";
        next.label = next.label || next.title.trim();
        next.slug = slug;
        next.title = next.title.trim();
        next.link = link;
      } else if (role === "brand") {
        next.kind = "logo";
        next.slug = "footer-logo";
        next.label = "Footer logo";
        next.link = next.link || "/";
      } else if (role === "note") {
        next.kind = "footer";
        next.slug = "footer-note";
        next.label = "Footer note";
        const email = String(next.subtitle || "").trim();
        next.subtitle = email;
        next.link = email ? `mailto:${email}` : "";
      } else if (role === "company") {
        next.kind = "footer";
        next.slug = "footer-company";
        next.label = "Footer company";
      }
      next.order = Number(next.order) || 0;
      next.published = next.published !== false && next.published !== "false";
      await onSave(next);
      setDraft(null);
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveHeadings(event) {
    event.preventDefault();
    setError("");
    const rows = [
      ["footer-label-visit", "Visit heading", headings.visit, labelVisit, 20],
      ["footer-label-follow", "Follow heading", headings.follow, labelFollow, 21],
      ["footer-label-company", "Company heading", headings.company, labelCompany, 22],
    ];
    try {
      for (const [slug, label, title, current, order] of rows) {
        await onSave({
          ...(current || {}),
          kind: "footer",
          slug,
          label,
          title: String(title || "").trim() || label.replace(" heading", ""),
          order: current?.order ?? order,
          published: true,
        });
      }
    } catch (err) {
      setError(err.message);
    }
  }

  function openNew(role) {
    setError("");
    const blanks = {
      phone: { role, kind: "footer", title: "", order: phones.length + 1, published: true },
      visit: { role, kind: "footer", title: "", link: "/", order: visits.length + 10, published: true },
      social: { role, kind: "social", title: "", link: "https://", order: socials.length + 1, published: true },
    };
    setDraft(blanks[role]);
  }

  return (
    <div className="footer-studio">
      <p className="muted">Edit the public footer here. Create adds a phone, page link, or social link. Delete removes it from the site. The green WhatsApp button stays on the WhatsApp tab.</p>
      {error ? <p className="error">{error}</p> : null}

      <Section
        title="Name"
        action={<button className="btn" type="button" onClick={() => setDraft({ role: "brand", ...(logo || { title: "Vishmi", subtitle: "Travels", link: "/", image: "", published: true, order: 2 }) })}>Edit</button>}
      >
        <p>{[logo?.title, logo?.subtitle].filter(Boolean).join(" ") || "Not set"}</p>
      </Section>

      <Section
        title="Welcome line, email, and legal note"
        action={<button className="btn" type="button" onClick={() => setDraft({ role: "note", ...(note || { title: "", subtitle: "", body: "", published: true, order: 1 }) })}>Edit</button>}
      >
        <p>{note?.title || "No welcome line"}</p>
        <p className="muted">{note?.subtitle || "No email"}</p>
        <p className="muted">{note?.body || "No legal note"}</p>
      </Section>

      <Section title="Phone numbers" action={<button className="btn" type="button" onClick={() => openNew("phone")}>Create</button>}>
        <Rows
          items={phones}
          columns={["Number"]}
          cells={(item) => [item.title]}
          onEdit={(item) => setDraft({ role: "phone", ...item })}
          onDelete={onDelete}
        />
      </Section>

      <Section title="Pages" action={<button className="btn" type="button" onClick={() => openNew("visit")}>Create</button>}>
        <Rows
          items={visits}
          columns={["Label", "Link"]}
          cells={(item) => [item.title, item.link]}
          onEdit={(item) => setDraft({ role: "visit", ...item })}
          onDelete={onDelete}
        />
      </Section>

      <Section title="Social links" action={<button className="btn" type="button" onClick={() => openNew("social")}>Create</button>}>
        <Rows
          items={socials}
          columns={["Name", "Link"]}
          cells={(item) => [item.title, item.link]}
          onEdit={(item) => setDraft({ role: "social", ...item })}
          onDelete={onDelete}
        />
      </Section>

      <Section
        title="Company"
        action={<button className="btn" type="button" onClick={() => setDraft({ role: "company", ...(company || { title: "", subtitle: "", body: "", published: true, order: 4 }) })}>Edit</button>}
      >
        <p className="footer-company">{company?.title || "Not set"}</p>
        <p className="muted">{company?.subtitle}</p>
        <p className="muted">{company?.body}</p>
      </Section>

      <form className="form footer-studio-card" onSubmit={saveHeadings}>
        <h2>Column headings</h2>
        <label>Pages column<input value={headings.visit} onChange={(e) => setHeadings({ ...headings, visit: e.target.value })} /></label>
        <label>Social column<input value={headings.follow} onChange={(e) => setHeadings({ ...headings, follow: e.target.value })} /></label>
        <label>Company column<input value={headings.company} onChange={(e) => setHeadings({ ...headings, company: e.target.value })} /></label>
        <button className="btn" type="submit">Save headings</button>
      </form>

      {draft ? (
        <form className="form footer-studio-card" onSubmit={submit}>
          <h2>{draft._id ? "Edit" : "Create"} {draft.role}</h2>
          {draft.role === "brand" ? (
            <>
              <label>First line<input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></label>
              <label>Second line<input value={draft.subtitle || ""} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} /></label>
              <label>Image address<input value={draft.image || ""} onChange={(e) => setDraft({ ...draft, image: e.target.value })} placeholder="Leave empty to use the words" /></label>
              <label>Link<input value={draft.link || "/"} onChange={(e) => setDraft({ ...draft, link: e.target.value })} /></label>
            </>
          ) : null}
          {draft.role === "note" ? (
            <>
              <label>Welcome line<textarea value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></label>
              <label>Email<input type="email" value={draft.subtitle || ""} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} /></label>
              <label>Legal note<textarea value={draft.body || ""} onChange={(e) => setDraft({ ...draft, body: e.target.value })} /></label>
            </>
          ) : null}
          {draft.role === "phone" ? (
            <label>Number<input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="+94 76784 5585" inputMode="tel" required /></label>
          ) : null}
          {draft.role === "visit" || draft.role === "social" ? (
            <>
              <label>{draft.role === "social" ? "Name" : "Label"}<input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} required /></label>
              <label>Link<input value={draft.link || ""} onChange={(e) => setDraft({ ...draft, link: e.target.value })} required /></label>
            </>
          ) : null}
          {draft.role === "company" ? (
            <>
              <label>Name<input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></label>
              <label>Registration<input value={draft.subtitle || ""} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} /></label>
              <label>Place<input value={draft.body || ""} onChange={(e) => setDraft({ ...draft, body: e.target.value })} /></label>
            </>
          ) : null}
          {draft.role === "phone" || draft.role === "visit" || draft.role === "social" ? (
            <>
              <label>Order<input type="number" value={draft.order ?? 0} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} /></label>
              <label>
                Show on the site
                <select value={String(draft.published !== false)} onChange={(e) => setDraft({ ...draft, published: e.target.value === "true" })}>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
            </>
          ) : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn" type="submit">Save</button>
            <button className="btn ghost" type="button" onClick={() => setDraft(null)}>Cancel</button>
            {draft._id ? (
              <button className="btn ghost" type="button" onClick={async () => { if (await onDelete(draft._id)) setDraft(null); }}>Delete</button>
            ) : null}
          </div>
        </form>
      ) : null}
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <section className="footer-studio-card">
      <div className="admin-bar">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Rows({ items, columns, cells, onEdit, onDelete }) {
  if (!items.length) return <p className="muted">Nothing here yet.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          {columns.map((column) => <th key={column}>{column}</th>)}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item._id || item.slug}>
            {cells(item).map((value, index) => (
              <td key={`${item._id || item.slug}-${index}`}>
                {value}
                {index === 0 && item.published === false ? <div className="muted">Hidden</div> : null}
              </td>
            ))}
            <td className="row-actions">
              <button type="button" onClick={() => onEdit(item)}>Edit</button>
              <button type="button" onClick={() => onDelete(item._id)}>Delete</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
